<template>
  <div class="form-control w-full">
    <input
      class="file-input file-input-bordered w-full"
      type="file"
      multiple
      accept="image/*"
      @change="handleFiles"
      :aria-label="t('inputs.imageUpload')" />

    <div v-if="files.length" class="mt-4 grid grid-cols-3 gap-4">
      <div
        v-for="(file, index) in files"
        :key="index"
        class="relative border rounded-lg shadow bg-base-100 overflow-hidden group">
        <img :src="file.url" :alt="t('inputs.imagePreviewAlt')" class="w-full h-auto object-cover" />
        <button
          class="absolute top-1 right-1 btn btn-xs btn-error opacity-0 group-hover:opacity-100 transition"
          @click="removeFile(index)"
          type="button"
          :aria-label="t('inputs.removeImage')">
          ✕
        </button>
      </div>
    </div>

    <p class="text-xs text-base-content/60 mt-2 italic">
      {{ t("inputs.imageUploadHint", { max: maxFiles }) }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
const { t } = useI18n();
interface PreviewFile {
  url: string;
  file: File;
}

const props = defineProps<{
  modelValue: string[]; // base64 or URLs
  maxFiles?: number;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string[]): void;
}>();

const maxFiles = props.maxFiles ?? 5;

const files = ref<PreviewFile[]>([]);

watch(
  () => props.modelValue,
  value => {
    if (!value?.length) files.value = [];
    // else: assume value already reflects base64 list
  },
  { immediate: true }
);

function handleFiles(event: Event) {
  const input = event.target as HTMLInputElement;
  const selected = input.files ? Array.from(input.files).slice(0, maxFiles) : [];

  selected.forEach(file => {
    const reader = new FileReader();
    reader.onload = () => {
      files.value.push({ url: reader.result as string, file });
      emit(
        "update:modelValue",
        files.value.map(f => f.url)
      );
    };
    reader.readAsDataURL(file);
  });

  input.value = ""; // Reset so re-selecting same file triggers `change`
}

function removeFile(index: number) {
  files.value.splice(index, 1);
  emit(
    "update:modelValue",
    files.value.map(f => f.url)
  );
}
</script>
